import React from 'react';
import { IProposedWorkItem } from '../../../types';
import { Header, TitleSize } from 'azure-devops-ui/Header';
import { Card } from 'azure-devops-ui/Card';
import { IHeaderCommandBarItem } from 'azure-devops-ui/HeaderCommandBar';
import { WorkItemIcon } from './WorkItemIcon';
import { FormItem } from 'azure-devops-ui/FormItem';
import MarkdownContent, { markdownToHtml } from '../../MarkdownContent';
import { Status, Statuses, StatusSize } from 'azure-devops-ui/Status';
import { Pill, PillVariant, PillSize } from 'azure-devops-ui/Pill';
import { PillGroup, PillGroupOverflow } from 'azure-devops-ui/PillGroup';
import { IColor } from 'azure-devops-extension-api';
import { useWorkItemManager } from '../../../hooks/useWorkItemManager';
import { useToast } from '../../../hooks/useToast';

export interface IProposedWorkItemProps {
  proposedWorkItem: IProposedWorkItem;
  onWorkItemCreated: (workItemId: string) => void;
}

export function ProposedWorkItem(props: IProposedWorkItemProps) {
  console.log('loading proposed work item', props);
  const workItemManager = useWorkItemManager();
  const toastProvider = useToast();

  function getWorkItemType() {
    switch (props.proposedWorkItem.type) {
      case 'feature':
        return 'Feature';
      case 'userstory':
        return 'User Story';
    }
  }

  async function getWorkItemFields() {
    // Add information about the parent work item since the SDK doesn't support adding relationships.
    const description = `${
      props.proposedWorkItem.description
    }\n\n*Parent work item: [${await workItemManager.getId()} - ${await workItemManager.getTitleField()}](${await workItemManager.getUrl()})*`;

    return {
      'System.Title': props.proposedWorkItem.title,
      'System.Description': await markdownToHtml(description),
      'Microsoft.VSTS.Common.AcceptanceCriteria': await markdownToHtml(
        props.proposedWorkItem.acceptanceCriteria
      ),
    };
  }

  async function createWorkItem() {
    try {
      const workItemId = await workItemManager.openWorkItemCreateForm(
        getWorkItemType(),
        await getWorkItemFields()
      );
      if (!!workItemId) props.onWorkItemCreated(workItemId);
    } catch (error) {
      toastProvider.showToast(error.toString(), 5000);
    }
  }

  const commandBarItems: IHeaderCommandBarItem[] = [
    {
      iconProps: {
        iconName: 'Add',
      },
      id: 'create',
      important: true,
      onActivate: () => {
        createWorkItem();
      },
      text: 'Create',
      tooltipProps: {
        text: 'Create work item in Azure DevOps',
      },
    },
  ];

  function getPillPropsByStatus(): { color: IColor; iconName: string } {
    switch (props.proposedWorkItem.status) {
      case 'Proposed':
        return {
          color: {
            red: 0,
            green: 120,
            blue: 212,
          },
          iconName: 'Info',
        };
      case 'Created':
        return {
          color: {
            red: 85,
            green: 163,
            blue: 98,
          },
          iconName: 'Accept',
        };
    }
  }

  const pillProps = getPillPropsByStatus();

  return (
    <>
      <Header
        title={props.proposedWorkItem.title}
        commandBarItems={commandBarItems}
        titleSize={TitleSize.Medium}
        titleIconProps={{ render: () => <WorkItemIcon {...props.proposedWorkItem} /> }}
        titleAriaLevel={3}
      />

      <div className='page-content page-content-top'>
        <div className='flex-column'>
          <PillGroup className='flex-row' overflow={PillGroupOverflow.wrap}>
            <Pill
              color={pillProps.color}
              size={PillSize.large}
              variant={PillVariant.colored}
              iconProps={{ iconName: pillProps.iconName }}
            >
              {props.proposedWorkItem.status}
            </Pill>
            {props.proposedWorkItem.workItemId && (
              <Pill
                size={PillSize.large}
                iconProps={{ iconName: 'WorkItem' }}
                onClick={() => workItemManager.openWorkItem(props.proposedWorkItem.workItemId)}
              >
                ID: {props.proposedWorkItem.workItemId}
              </Pill>
            )}
          </PillGroup>
          <div className='spacer' />
          <FormItem label='Description'>
            <div className='large-text-box ado-border description'>
              <MarkdownContent content={props.proposedWorkItem.description} />
            </div>
          </FormItem>
          <div className='spacer' />
          <FormItem label='Acceptance Criteria'>
            <div className='large-text-box ado-border description'>
              <MarkdownContent content={props.proposedWorkItem.acceptanceCriteria} />
            </div>
          </FormItem>
        </div>
      </div>
    </>
  );
}
